import axios from 'axios';
import crProgramsData from './crPrograms.json';

const crPrograms = crProgramsData;

/**
 * Match a scraped show title to an 881903.com program record
 */
export function get881903Program(titleOrName) {
  if (!titleOrName || typeof titleOrName !== 'string') return null;

  // 1. Direct key lookup
  if (crPrograms[titleOrName]) {
    const p = crPrograms[titleOrName];
    return {
      ...p,
      url: `https://www.881903.com/program/${p.id}/${encodeURIComponent(p.slug || p.title)}`
    };
  }

  // 2. Strip station prefix (叱咤903, 雷霆881, 商業電台)
  const clean = titleOrName.replace(/^(叱咤903|雷霆881|商業電台)\s*/, '').trim();
  if (crPrograms[clean]) {
    const p = crPrograms[clean];
    return {
      ...p,
      url: `https://www.881903.com/program/${p.id}/${encodeURIComponent(p.slug || p.title)}`
    };
  }

  // 3. Normalized comparison (ignore spaces, hyphens, dots, casing)
  const normTarget = clean.replace(/[\s\.\-_!！?？]/g, '').toLowerCase();
  for (const [key, prog] of Object.entries(crPrograms)) {
    const normKey = key.replace(/[\s\.\-_!！?？]/g, '').toLowerCase();
    if (normTarget === normKey || normTarget.includes(normKey) || normKey.includes(normTarget)) {
      return {
        ...prog,
        url: `https://www.881903.com/program/${prog.id}/${encodeURIComponent(prog.slug || prog.title)}`
      };
    }
  }

  return null;
}

/**
 * Fetch half-hour program summaries for a specific 881903 program on a given date
 */
export async function fetch881903ProgramSummary(programId, dateStr) {
  if (!programId || !dateStr) return { success: false, sections: [] };

  const cacheKey = `cr_summary_${programId}_${dateStr}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // ignore
  }

  const targetApi = `https://www.881903.com/api/programarchives/listwithnotice/${programId}?next_section_date=${dateStr}`;

  // Proxy endpoints list
  const proxyEndpoints = [
    `/api/proxy?url=${encodeURIComponent(targetApi)}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(targetApi)}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(targetApi)}`
  ];

  for (const endpoint of proxyEndpoints) {
    try {
      const res = await axios.get(endpoint, {
        timeout: 8000,
        validateStatus: status => status === 200
      });

      let data = res.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          continue;
        }
      }

      const archives = data?.response?.program_archives || data?.program_archives || [];
      const archive = archives.find(a => a.section_date === dateStr);

      if (archive && archive.program_archive_sections) {
        const sections = archive.program_archive_sections.map(s => {
          let title = s.caption || s.episode_title;
          if (!title && s.content_data) {
            try {
              const cd = typeof s.content_data === 'string' ? JSON.parse(s.content_data) : s.content_data;
              title = cd.title || cd.episode_title;
            } catch (e) {}
          }

          const start = (s.display_start_time || '').slice(0, 5);
          const end = (s.display_end_time || '').slice(0, 5);
          const time = start && end ? `${start} - ${end}` : s.section_no ? `Part ${s.section_no}` : '';

          return {
            time,
            title: title || '',
            description: s.description || ''
          };
        });

        const result = { success: true, sections };
        try {
          localStorage.setItem(cacheKey, JSON.stringify(result));
        } catch (e) {}
        return result;
      } else if (archives.length > 0) {
        // Date not in this archive response
        return { success: false, sections: [], message: 'No official rundown posted for this date.' };
      }
    } catch (err) {
      // Try next endpoint
    }
  }

  return { success: false, sections: [], message: 'Unable to load 881903 rundown.' };
}
